import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async (event) => {
    // If the event provides updated section HTML, swap it directly
    if (event?.detail?.sections?.['cart-drawer']) {
      this.#renderCartSection(event.detail.sections['cart-drawer']);
    } else {
      // Fallback: Fetch fresh cart drawer section from Shopify
      await this.refresh();
    }

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Re-fetches the latest cart-drawer section HTML from Shopify to keep state synced.
   */
  async refresh() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}?sections=cart-drawer`);
      const sections = await response.json();
      if (sections['cart-drawer']) {
        this.#renderCartSection(sections['cart-drawer']);
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  // Declared properly as a private method
  #renderCartSection(htmlString) {
    const parsedHTML = new DOMParser().parseFromString(htmlString, 'text/html');
    const newContent = parsedHTML.querySelector('cart-drawer-component')?.innerHTML;
    if (newContent) {
      this.innerHTML = newContent;
    }
  }

  async open() {
    // Fetch fresh cart HTML every time drawer is opened manually (e.g. clicking header cart icon)
    await this.refresh();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}